import React, { memo, useCallback } from 'react';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { Image, View } from 'react-native';
import Text from 'design-system/Text';
import Squircle from 'design-system/Squircle';
import StatusBar from 'design-system/StatusBar';
import { IconRocket } from 'design-system/icons/IconRocket';
import { AppView, CardView, IconArrow, rem, Spacer } from 'design-system';
import IconEmmaWallet from 'design-system/icons/IconEmmaWallet';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import Walkthrough from 'features/feed/components/walkthrough/Walkthrough';
import useWalkthroughLayout from 'features/feed/components/walkthrough/useWalkthroughLayout';
import { useShowWalkthroughV6 } from 'features/feed/components/walkthrough/hooks/useShowWalkthroughV6';
import InvestPerformanceCard from './InvestPerformanceCard';
import FSCSCompensationInfoView from './FSCSCompensationInfoView';
import SetupInvestmentAccountButton from './SetupInvestmentAccountButton';
import { NavigationHeaderWithAvatarAndHelp } from './NavigationHeaderWithAvatar';
import { AccountTypeDescription, AccountTypeDisplayLong } from '../constants';
import useNavigateToIntroducingInvestmentAccount from '../hooks/useNavigateToIntroducingInvestmentAccount';
import { blockInvestActionOnWeb } from '../webGate';
const discoverEntryPointGraphics = require('../img/discoverEntryPointGraphics.png');
const InvestIntroView = () => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const scrollY = useSharedValue(0);
    const navigation = useMainStackNavigation();
    const navigateToIntroducingInvestmentAccount = useNavigateToIntroducingInvestmentAccount();
    const showWalkthroughV6 = useShowWalkthroughV6();
    const { itemPositions, scrollHeight, scrollLayoutHeight, onLayout } = useWalkthroughLayout();
    const { paddingHorizontalStyle } = useAppFrameDimensions(true);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const handleLayout = useCallback((event) => {
        onLayout('investTabInfo', event);
    }, [onLayout]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    });
    return (<AppView withFrame={false}>
      <StatusBar animated barStyle="dark-content"/>
      <NavigationHeaderWithAvatarAndHelp showTitle scrollY={scrollY}/>
      <Animated.ScrollView scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} onScroll={scrollHandler}>
        <Spacer h={24}/>
        <View style={styles.topContainer} onLayout={handleLayout}>
          <Text Text-24 centered>
            Make your money work harder
          </Text>
          <Spacer h={8}/>
          <Text TextThin-16 Secondary centered>
            Grow your money over time with globally diversified funds. Capital at risk.
          </Text>
        </View>
        <Spacer h={24}/>
        <InvestPerformanceCard />
        <CardView withPadding style={styles.investCard}>
          <View style={styles.cardRow}>
            <View style={styles.cardLeft}>
              <Text Text-16>Invest your way</Text>
              <Text TextThin-14 Secondary style={styles.descriptionContainer}>
                Start with ready-made funds or explore ETFs across global markets.
              </Text>
              <Spacer h={16}/>
              <View style={styles.buttonContainer}>
                <Button hug small square brandReversed title="Discover Funds & ETFs" onPress={() => navigation.navigate('InvestDiscover')}/>
              </View>
            </View>
            <Spacer w={8}/>
            <Image source={discoverEntryPointGraphics} style={styles.cardImage}/>
          </View>
        </CardView>
        <Text Text-16 style={styles.sectionTitle}>
          Choose your account
        </Text>
        <TouchableBounceCard onDark withPadding style={styles.investCard} onPress={() => navigateToIntroducingInvestmentAccount('ISA')}>
          <View style={styles.investCardRow}>
            <IconEmmaWallet />
            <Spacer w={16}/>
            <View style={styles.cardLeft}>
              <Text Text-16>{AccountTypeDisplayLong.ISA}</Text>
              {AccountTypeDescription.ISA}
            </View>
            <Spacer w={16}/>
            <IconArrow direction="right"/>
          </View>
        </TouchableBounceCard>
        <TouchableBounceCard onDark withPadding style={styles.investCard} onPress={() => navigateToIntroducingInvestmentAccount('GIA')}>
          <View style={styles.investCardRow}>
            <IconEmmaWallet />
            <Spacer w={16}/>
            <View style={styles.cardLeft}>
              <Text Text-16>{AccountTypeDisplayLong.GIA}</Text>
              <Text TextThin-14 Secondary>
                {AccountTypeDescription.GIA}
              </Text>
            </View>
            <Spacer w={16}/>
            <IconArrow direction="right"/>
          </View>
        </TouchableBounceCard>
        <TouchableBounceCard onDark withPadding style={styles.investCard} onPress={() => navigateToIntroducingInvestmentAccount('JISA')}>
          <View style={styles.investCardRow}>
            <IconEmmaWallet />
            <Spacer w={16}/>
            <View style={styles.cardLeft}>
              <Text Text-16>{AccountTypeDisplayLong.JISA}</Text>
              {AccountTypeDescription.JISA}
            </View>
            <Spacer w={16}/>
            <IconArrow direction="right"/>
          </View>
        </TouchableBounceCard>
        <Text Text-16 style={styles.sectionTitle}>
          Invest on autopilot
        </Text>
        <CardView style={styles.investCard} withPadding>
          <Squircle color={colors.background.dark}>
            <IconRocket color={colors.text.investment}/>
          </Squircle>
          <Spacer h={16}/>
          <Text Text-16>Autoinvest</Text>
          <Text TextThin-14 Secondary>
            Invest automatically every month
          </Text>
          <Spacer h={16}/>
          <View style={styles.buttonContainer}>
            <Button hug brandReversed small square title="Start autoinvest" onPress={() => {
            if (blockInvestActionOnWeb('Set up Autoinvest'))
                return;
            navigation.navigate('AutoinvestIntro', {});
        }}/>
          </View>
        </CardView>
        <View style={styles.horizontalPadding}>
          <FSCSCompensationInfoView />
        </View>
      </Animated.ScrollView>
      <SetupInvestmentAccountButton brand floating isInTab/>
      {!!showWalkthroughV6 && (<Walkthrough isInTab scrollHeight={scrollHeight} itemPositions={itemPositions} scrollLayoutHeight={scrollLayoutHeight}/>)}
    </AppView>);
};
export default memo(InvestIntroView);
const styleSet = createStyleSheets(() => ({
    horizontalPadding: {
        paddingHorizontal: rem(16),
    },
    topContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: rem(16),
    },
    cardRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(8),
    },
    cardLeft: {
        flex: 1,
    },
    buttonContainer: {
        alignSelf: 'flex-start',
    },
    cardImage: {
        width: rem(80),
        height: rem(80),
    },
    sectionTitle: {
        padding: rem(16),
        paddingBottom: rem(8),
    },
    headerContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: rem(16),
    },
    investCardRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    investCard: {
        marginHorizontal: rem(16),
        marginVertical: rem(8),
    },
    descriptionContainer: {
        paddingVertical: rem(4),
    },
}));
